import { RealtimeUpdateOrigin } from '@common/api/realtime';

export interface RealtimeV2EntityUpdateContext {
  entityName: string;
  entityId: string;
  version?: number;
  origin?: RealtimeUpdateOrigin;
  sameOrigin: boolean;
}

const entityUpdateContexts = new Map<string, RealtimeV2EntityUpdateContext>();
const localMutations = new Map<string, Map<string, number>>();

export function recordRealtimeV2EntityUpdateContext(context: RealtimeV2EntityUpdateContext): void {
  // Ricorda da quale messaggio realtime arriva l'ultima versione vista per l'entita'.
  // La form usa questa informazione per capire se sta guardando un echo del proprio salvataggio.
  entityUpdateContexts.set(context.entityId, context);
}

export function getRealtimeV2EntityUpdateContext(entityId: string, version?: number): RealtimeV2EntityUpdateContext | undefined {
  const context = entityUpdateContexts.get(entityId);
  if (!context) {
    return undefined;
  }

  if (version === undefined || context.version === undefined) {
    return context;
  }

  return context.version === version ? context : undefined;
}

export function markRealtimeV2LocalMutation(entityId: string, fields: string[], mutationSeq: number): void {
  if (!entityId || !fields.length) {
    return;
  }

  // Ogni campo tiene solo il salvataggio locale piu' recente.
  // Se torna un echo piu' vecchio dal backend, non deve coprire cio' che l'utente ha scritto dopo.
  let entityMutations = localMutations.get(entityId);
  if (!entityMutations) {
    entityMutations = new Map<string, number>();
    localMutations.set(entityId, entityMutations);
  }

  for (const field of fields) {
    const currentSeq = entityMutations.get(field);
    if (currentSeq === undefined || mutationSeq > currentSeq) {
      entityMutations.set(field, mutationSeq);
    }
  }
}

export function getRealtimeV2LocalMutationSeq(entityId: string, field: string): number | undefined {
  return localMutations.get(entityId)?.get(field);
}

export function acknowledgeRealtimeV2LocalMutation(entityId: string, field: string, mutationSeq?: number): void {
  const entityMutations = localMutations.get(entityId);
  const currentSeq = entityMutations?.get(field);
  if (!entityMutations || currentSeq === undefined) {
    return;
  }

  // Un ritorno del backend chiude la mutation locale solo se e' almeno recente quanto quella in sospeso.
  if (mutationSeq === undefined || currentSeq <= mutationSeq) {
    entityMutations.delete(field);
  }

  if (entityMutations.size === 0) {
    localMutations.delete(entityId);
  }
}
